import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, AlertTriangle, X, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import api from "../api/axios";

export default function DeleteAccountModal({ isOpen, onClose }) {
    const { t } = useLanguage();
    const [sliderValue, setSliderValue] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const handleAccountClosure = async () => {
        try {
            setIsLoading(true);
            await api.delete("/auth/close-account");
            window.location.href = "/login?closed=true";
        } catch (err) {
            console.error("Closure failed:", err);
            setSliderValue(0);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/40 backdrop-blur-md"
                />

                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border border-red-100 dark:border-red-900/20"
                >
                    <div className="p-6 md:p-8 space-y-6">
                        <div className="flex justify-between items-start">
                            <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded-2xl">
                                <AlertTriangle className="w-6 h-6 text-red-500" />
                            </div>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {t('set_close_warning_title') || "A Moment of Pause?"}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                {t('set_close_warning_desc') || "Your account will enter a 30-day deactivation period. You can recover it by logging in within 30 days. After that, it will be permanently deleted. (Audit logs may be retained for safety purposes)."}
                            </p>
                        </div>

                        <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-2xl border border-red-100 dark:border-red-900/20">
                            <p className="text-[11px] text-red-600 dark:text-red-400 font-medium">
                                Warning: This action will end your active journey. Reflections will be preserved for 30 days only.
                            </p>
                        </div>

                        {/* Slide to Confirm */}
                        <div className="relative h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden flex items-center px-2">
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    {sliderValue > 90 ? "Release to Delete" : t('set_slide_to_confirm')}
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={sliderValue}
                                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                                onMouseUp={() => {
                                    if (sliderValue < 95) setSliderValue(0);
                                    else handleAccountClosure();
                                }}
                                onTouchEnd={() => {
                                    if (sliderValue < 95) setSliderValue(0);
                                    else handleAccountClosure();
                                }}
                                className="absolute inset-0 opacity-0 cursor-pointer z-10"
                            />
                            <motion.div 
                                className="h-12 w-12 bg-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/20"
                                style={{ x: `${sliderValue * 2.8}px` }}
                            >
                                {isLoading ? (
                                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                                ) : (
                                    <Trash2 className="w-6 h-6 text-white" />
                                )}
                            </motion.div>
                        </div>

                        <button 
                            onClick={onClose}
                            className="w-full py-4 text-xs font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                            {t('set_cancel')}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
